// src/components/EngagementPanel.js
//
// v3.5.99.6 — Client engagement panel on the quote page.
//
// Surfaces the proposal + Service Order engagement history that's been
// captured in activity_log since v3.5.99.0 (proposal views) and v3.5.99.6
// (SO views/sign/decline). Reps now see "Viewed 3× — last 5/13 09:15" type
// status inline without having to navigate to the Activity Log page.
//
// Data source: a single Supabase query on activity_log filtered by quote_id
// and the relevant engagement actions. Aggregates entries into a per-channel
// summary (proposal | service order). Polls every 30 seconds so reps see
// near-realtime updates after sending a proposal/SO.
//
// Mount: each quote page mounts this between the Documents card and the
// SignWell signature card on the right column. Hidden when the quote has
// no engagement events yet (empty state → return null; rep doesn't need
// a blank card on a freshly-created quote).

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const ENGAGEMENT_ACTIONS = [
  // Proposal lifecycle
  'PROPOSAL_SENT',
  'PROPOSAL_VIEWED',
  'PROPOSAL_RE_VIEWED',
  'PROPOSAL_APPROVED',
  // Service Order lifecycle
  'SERVICE_ORDER_VIEWED',
  'SERVICE_ORDER_RE_VIEWED',
  'SIGNATURE_COMPLETED',  // existing — written by signwellWebhook on sign
  'SIGNATURE_DECLINED',   // existing — written by signwellWebhook on decline
];

const PROPOSAL_ACTIONS = ['PROPOSAL_SENT', 'PROPOSAL_VIEWED', 'PROPOSAL_RE_VIEWED', 'PROPOSAL_APPROVED'];

function formatRelative(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `today at ${time}`;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `yesterday at ${time}`;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) + ` at ${time}`;
}

// v3.5.99.7.12: Engagement drill-down helpers.
//
// groupIntoSessions — collapses consecutive view events into "sessions"
// based on a time-gap threshold (default 5 minutes). Input: events sorted
// newest-first. Output: sessions newest-first, with each session's events
// also newest-first. Within a session, latestTs is the most-recent event
// time and earliestTs is the oldest; gap is measured between the session's
// earliest event and the next (older) candidate event.
function groupIntoSessions(events, gapMinutes = 5) {
  const gapMs = gapMinutes * 60_000;
  const sessions = [];
  let current = null;
  for (const e of events) {
    const ts = new Date(e.created_at).getTime();
    if (!current) {
      current = { latestTs: ts, earliestTs: ts, events: [e] };
      sessions.push(current);
    } else if ((current.earliestTs - ts) <= gapMs) {
      current.events.push(e);
      current.earliestTs = ts;
    } else {
      current = { latestTs: ts, earliestTs: ts, events: [e] };
      sessions.push(current);
    }
  }
  return sessions;
}

// Render a session's time range. Single-event sessions show one timestamp;
// multi-event sessions show "from X to Y" (or just "around X" when both
// timestamps render to the same human-readable string).
function formatSessionRange(session) {
  const start = formatRelative(new Date(session.latestTs).toISOString());
  if (session.events.length === 1) return start;
  const end = formatRelative(new Date(session.earliestTs).toISOString());
  if (start === end) return `around ${start}`;
  // Display as "earliest → latest" so reading left-to-right matches the
  // chronological flow even though the underlying array is newest-first.
  return `${end} → ${start}`;
}

// Parse a User-Agent string into a short "Browser / OS" label. Order
// matters: Edge contains "Chrome" in its UA, iOS Safari contains "Safari"
// not "iPhone Safari", etc. Returns null on empty input.
function parseUA(ua) {
  if (!ua) return null;
  const browser =
    /Edg\//.test(ua)    ? 'Edge'    :
    /Chrome/.test(ua)   ? 'Chrome'  :
    /Firefox/.test(ua)  ? 'Firefox' :
    /Safari/.test(ua)   ? 'Safari'  :
    'Other browser';
  const os =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS'     :
    /Android/.test(ua)          ? 'Android' :
    /Mac OS X/.test(ua)         ? 'macOS'   :
    /Windows/.test(ua)          ? 'Windows' :
    /Linux/.test(ua)            ? 'Linux'   :
    'unknown OS';
  return `${browser} / ${os}`;
}

// Build the technical-detail summary for a session: collapse distinct IPs
// and UAs across events into a comma-separated list (most viewing sessions
// will have 1 IP + 1 UA, but defensively handle the edge case of e.g. a
// client opening the link on phone then desktop within the same 5-min
// window). Returns null if neither field is present on any event.
function summarizeSessionTechnical(session) {
  const ips = new Set();
  const uas = new Set();
  for (const e of session.events) {
    const m = e.metadata || {};
    if (m.ip)         ips.add(m.ip);
    if (m.user_agent) {
      const parsed = parseUA(m.user_agent);
      if (parsed) uas.add(parsed);
    }
  }
  const ipStr = ips.size ? [...ips].join(', ') : '';
  const uaStr = uas.size ? [...uas].join(', ') : '';
  if (!ipStr && !uaStr) return null;
  return [ipStr, uaStr].filter(Boolean).join(' · ');
}

export default function EngagementPanel({ quoteId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // v3.5.99.7.12: drill-down state. Each section (proposal, SO) tracks its
  // own expanded flag + technical-details toggle so users can mix-and-match
  // (e.g. proposal expanded with raw IPs visible, SO collapsed).
  const [expandedProposal, setExpandedProposal] = useState(false);
  const [expandedSo, setExpandedSo] = useState(false);
  const [showTechProposal, setShowTechProposal] = useState(false);
  const [showTechSo, setShowTechSo] = useState(false);

  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;

    async function fetchEntries() {
      // v3.5.99.6.1: TWO independent queries — neither short-circuits the
      // other. The original v3.5.99.6 design errored out of the SO-side
      // query on any failure and never ran the proposal-side query, which
      // hid the panel completely when the SO side returned empty/errored.
      //
      // Query A: SO-side entries are written with entity_id=quote.id, so
      //          filter directly on entity_id.
      // Query B: Proposal-side entries are written with
      //          entity_type='proposal' and entity_id=proposal.id, so the
      //          linker is metadata.quote_id. JSONB path filter needs
      //          .filter('metadata->>quote_id', 'eq', value) — chainable
      //          .eq() syntax with the path operator unreliable in
      //          supabase-js v2 under URL encoding.
      const [soResult, propResult] = await Promise.all([
        supabase
          .from('activity_log')
          .select('action, created_at, metadata')
          .eq('entity_id', quoteId)
          .in('action', ENGAGEMENT_ACTIONS)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('activity_log')
          .select('action, created_at, metadata')
          .in('action', PROPOSAL_ACTIONS)
          .filter('metadata->>quote_id', 'eq', quoteId)
          .order('created_at', { ascending: false })
          .limit(50),
      ]);

      if (cancelled) return;

      if (soResult.error) {
        console.warn('EngagementPanel: SO-side query failed', soResult.error);
      }
      if (propResult.error) {
        console.warn('EngagementPanel: proposal-side query failed', propResult.error);
      }

      const soRows   = Array.isArray(soResult.data)   ? soResult.data   : [];
      const propRows = Array.isArray(propResult.data) ? propResult.data : [];

      // Merge + dedupe by created_at + action (a single event could land
      // in both queries in edge cases).
      const seen = new Set();
      const all = [...soRows, ...propRows].filter(e => {
        const k = `${e.action}:${e.created_at}`;
        if (seen.has(k)) return false; seen.add(k); return true;
      });
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setEntries(all);
      setLoading(false);
    }

    fetchEntries();
    const id = setInterval(fetchEntries, 30000); // poll every 30s
    return () => { cancelled = true; clearInterval(id); };
  }, [quoteId]);

  if (loading || entries.length === 0) return null;

  // Aggregate per-channel state.
  // For proposal: sent timestamp, count views (first + re-engagement = total views), find
  // most-recent view, find approval.
  // For SO: count views, find most-recent view, find signed/declined.
  const propSent     = entries.find(e => e.action === 'PROPOSAL_SENT');
  const propViews    = entries.filter(e => e.action === 'PROPOSAL_VIEWED' || e.action === 'PROPOSAL_RE_VIEWED');
  const propApproved = entries.find(e => e.action === 'PROPOSAL_APPROVED');
  const soViews      = entries.filter(e => e.action === 'SERVICE_ORDER_VIEWED' || e.action === 'SERVICE_ORDER_RE_VIEWED');
  const soSigned     = entries.find(e => e.action === 'SIGNATURE_COMPLETED');
  const soDeclined   = entries.find(e => e.action === 'SIGNATURE_DECLINED');

  // Proposal view count: prefer the most-recent metadata.view_count from a
  // PROPOSAL_RE_VIEWED entry (which carries the running count); else the
  // PROPOSAL_VIEWED entry's implicit count of 1; else 0.
  const propViewCount = propViews[0]?.metadata?.view_count
                     || (propViews.length > 0 ? propViews.length : 0);
  const propLastView  = propViews[0]?.created_at;
  const soViewCount   = soViews[0]?.metadata?.view_count
                     || (soViews.length > 0 ? soViews.length : 0);
  const soLastView    = soViews[0]?.created_at;

  // Don't render if both channels are empty. (Already short-circuited above
  // via entries.length === 0, but this guards against the edge case where
  // we have entries for some other reason.)
  const hasProposalActivity = propSent || propViews.length > 0 || propApproved;
  const hasSoActivity       = soViews.length > 0 || soSigned || soDeclined;
  if (!hasProposalActivity && !hasSoActivity) return null;

  return (
    <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:'10px 12px', marginBottom:10 }}>
      <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6b7280', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span>📊 Client Engagement</span>
        <span style={{ fontSize:8, fontWeight:500, color:'#9ca3af', textTransform:'none', letterSpacing:0 }}>refreshes every 30s</span>
      </div>

      {hasProposalActivity && (
        <div style={{ padding:'8px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb', marginBottom: hasSoActivity ? 6 : 0 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#0f1e3c', marginBottom:4, display:'flex', alignItems:'center', gap:5 }}>
            <span>📩 Proposal</span>
            {propApproved && (
              <span style={{ fontSize:8, fontWeight:700, color:'#166534', background:'#dcfce7', padding:'1px 5px', borderRadius:3 }}>Approved ✓</span>
            )}
          </div>
          {propSent && (
            <div style={{ fontSize:10, color:'#374151', marginTop:2, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ color:'#9ca3af' }}>📤</span>
              <span style={{ color:'#6b7280' }}>Sent {formatRelative(propSent.created_at)}</span>
            </div>
          )}
          {propViews.length > 0 && (
            <>
              {/* v3.5.99.7.12: clickable drill-down trigger. Reads naturally
                  as a button (cursor:pointer, hover background, caret).
                  Toggles expandedProposal state. */}
              <div
                onClick={() => setExpandedProposal(v => !v)}
                style={{ fontSize:10, color:'#374151', marginTop:2, display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'2px 4px', marginLeft:-4, borderRadius:3, transition:'background 0.1s', userSelect:'none' }}
                onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ color:'#9ca3af' }}>👁</span>
                <span>Viewed <strong>{propViewCount}×</strong></span>
                <span style={{ color:'#9ca3af' }}>·</span>
                <span style={{ color:'#6b7280' }}>last {formatRelative(propLastView)}</span>
                <span style={{ marginLeft:'auto', color:'#6b7280', fontSize:9 }}>{expandedProposal ? '▾' : '▸'}</span>
              </div>
              {expandedProposal && (() => {
                const sessions = groupIntoSessions(propViews);
                return (
                  <div style={{ marginTop:6, paddingTop:6, paddingLeft:18, borderTop:'1px dashed #e5e7eb' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                      <span style={{ fontSize:9, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.05em' }}>
                        {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} · 5-min window
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowTechProposal(v => !v)}
                        style={{ fontSize:9, color: showTechProposal ? '#0f1e3c' : '#6b7280', background:'transparent', border:`1px solid ${showTechProposal ? '#0f1e3c' : '#d1d5db'}`, borderRadius:3, padding:'1px 6px', cursor:'pointer', fontWeight:600 }}>
                        ⚙ {showTechProposal ? 'Hide' : 'Show'} technical
                      </button>
                    </div>
                    {sessions.map((s, i) => {
                      const tech = showTechProposal ? summarizeSessionTechnical(s) : null;
                      return (
                        <div key={i} style={{ fontSize:10, color:'#475569', marginBottom: showTechProposal ? 5 : 2 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span>{formatSessionRange(s)}</span>
                            <span style={{ fontSize:9, color:'#6b7280', background:'#f3f4f6', padding:'0 5px', borderRadius:3, fontWeight:600 }}>
                              {s.events.length} {s.events.length === 1 ? 'view' : 'views'}
                            </span>
                          </div>
                          {showTechProposal && (
                            <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, fontFamily:'DM Mono, monospace' }}>
                              {tech || '(no IP / user-agent captured)'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {propViews.length >= 50 && (
                      <div style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic', marginTop:4 }}>
                        Showing the 50 most recent view events.
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {propApproved && (
            <div style={{ fontSize:10, color:'#374151', marginTop:2, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ color:'#166534' }}>✓</span>
              <span style={{ color:'#6b7280' }}>Approved {formatRelative(propApproved.created_at)}{propApproved.metadata?.approver_name ? ` by ${propApproved.metadata.approver_name}` : ''}</span>
            </div>
          )}
        </div>
      )}

      {hasSoActivity && (
        <div style={{ padding:'8px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#0f1e3c', marginBottom:4, display:'flex', alignItems:'center', gap:5 }}>
            <span>📋 Service Order</span>
            {soSigned && !soDeclined && (
              <span style={{ fontSize:8, fontWeight:700, color:'#166534', background:'#dcfce7', padding:'1px 5px', borderRadius:3 }}>Signed ✓</span>
            )}
            {soDeclined && (
              <span style={{ fontSize:8, fontWeight:700, color:'#991b1b', background:'#fef2f2', padding:'1px 5px', borderRadius:3 }}>Declined ✗</span>
            )}
          </div>
          {soViews.length > 0 && (
            <>
              {/* v3.5.99.7.12: same drill-down pattern as the proposal section. */}
              <div
                onClick={() => setExpandedSo(v => !v)}
                style={{ fontSize:10, color:'#374151', marginTop:2, display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'2px 4px', marginLeft:-4, borderRadius:3, transition:'background 0.1s', userSelect:'none' }}
                onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ color:'#9ca3af' }}>👁</span>
                <span>Viewed <strong>{soViewCount}×</strong></span>
                <span style={{ color:'#9ca3af' }}>·</span>
                <span style={{ color:'#6b7280' }}>last {formatRelative(soLastView)}</span>
                <span style={{ marginLeft:'auto', color:'#6b7280', fontSize:9 }}>{expandedSo ? '▾' : '▸'}</span>
              </div>
              {expandedSo && (() => {
                const sessions = groupIntoSessions(soViews);
                return (
                  <div style={{ marginTop:6, paddingTop:6, paddingLeft:18, borderTop:'1px dashed #e5e7eb' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                      <span style={{ fontSize:9, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.05em' }}>
                        {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} · 5-min window
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowTechSo(v => !v)}
                        style={{ fontSize:9, color: showTechSo ? '#0f1e3c' : '#6b7280', background:'transparent', border:`1px solid ${showTechSo ? '#0f1e3c' : '#d1d5db'}`, borderRadius:3, padding:'1px 6px', cursor:'pointer', fontWeight:600 }}>
                        ⚙ {showTechSo ? 'Hide' : 'Show'} technical
                      </button>
                    </div>
                    {sessions.map((s, i) => {
                      const tech = showTechSo ? summarizeSessionTechnical(s) : null;
                      return (
                        <div key={i} style={{ fontSize:10, color:'#475569', marginBottom: showTechSo ? 5 : 2 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span>{formatSessionRange(s)}</span>
                            <span style={{ fontSize:9, color:'#6b7280', background:'#f3f4f6', padding:'0 5px', borderRadius:3, fontWeight:600 }}>
                              {s.events.length} {s.events.length === 1 ? 'view' : 'views'}
                            </span>
                          </div>
                          {showTechSo && (
                            <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, fontFamily:'DM Mono, monospace' }}>
                              {tech || '(no IP / user-agent captured)'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {soViews.length >= 50 && (
                      <div style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic', marginTop:4 }}>
                        Showing the 50 most recent view events.
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {soSigned && (
            <div style={{ fontSize:10, color:'#374151', marginTop:2, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ color:'#166534' }}>✓</span>
              <span style={{ color:'#6b7280' }}>Signed {formatRelative(soSigned.created_at)}{soSigned.metadata?.countersigned ? ' · countersigned' : ''}</span>
            </div>
          )}
          {soDeclined && (
            <div style={{ fontSize:10, color:'#374151', marginTop:2, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ color:'#991b1b' }}>⚠</span>
              <span style={{ color:'#6b7280' }}>Declined {formatRelative(soDeclined.created_at)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
